import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaLayerGroup, FaListOl, FaFileAlt, FaInfoCircle, FaCheckCircle, FaUndo, FaBookOpen } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import CustomBreadcrumb from '../../../components/CustomBreadcrumb';
import SubjectCard from '../../../components/SubjectCard';
import FilterTopicTree from '../examPappers/FilterTopicTree';
import { getSubjects, getSubjectDetailsById } from '../../../api/user/subject.api';
import { buildPickerTreeFromSubjectDetail } from '../../../utils/subjectSyllabusTree';
import { bookOpenIcon, homeIcon } from '../../../constants/icons';

const EXAM_SYLLABUS_LEVELS = ['Topic', 'Subtopic', 'Chapter', 'Section'];

const PracticeGenerator = () => {
  const navigate = useNavigate();

  // State: Subjects
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [subjectError, setSubjectError] = useState(null);

  // State: Selected Subject & Tree
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState(null);

  // State: Selections
  const [selectedChapters, setSelectedChapters] = useState({});
  const [mode, setMode] = useState('total'); // 'total' or 'per-topic'
  const [totalCount, setTotalCount] = useState(10);
  const [perTopicCounts, setPerTopicCounts] = useState({});
  const [showAllTopics, setShowAllTopics] = useState(false);

  const breadcrumbItems = [
    { label: <>{homeIcon}</>, path: "/" },
    { label: "Practice Generator", path: "/practice-generator" },
  ];

  // Fetch subjects on mount
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await getSubjects();
        setSubjects(response.data || []);
      } catch (err) {
        setSubjectError("Failed to fetch subjects. Please try again later.");
        console.error(err);
      } finally {
        setLoadingSubjects(false);
      }
    };
    fetchSubjects();
  }, []);

  // Fetch topics when subject is selected
  useEffect(() => {
    if (!selectedSubject) return;

    const fetchTree = async () => {
      setLoadingTree(true);
      setTreeError(null);
      try {
        const detail = await getSubjectDetailsById(selectedSubject._id);
        const fromDetail = buildPickerTreeFromSubjectDetail(detail);
        setTreeData(fromDetail);
        setSelectedChapters({});
        setPerTopicCounts({});
        setShowAllTopics(false);
      } catch (e) {
        console.error(e);
        setTreeError("Failed to load syllabus for this subject.");
      } finally {
        setLoadingTree(false);
      }
    };

    fetchTree();
  }, [selectedSubject]);

  // Derived state: Labels for selected topics
  const selectedTopicLabels = useMemo(() => {
    const labels = [];
    const findSelectedNodes = (nodes) => {
      nodes.forEach(node => {
        if (!node.children?.length && selectedChapters[node.id]) {
          labels.push({ id: node.id, label: node.label });
        }
        if (node.children) findSelectedNodes(node.children);
      });
    };
    findSelectedNodes(treeData);
    return labels;
  }, [treeData, selectedChapters]);

  const totalQuestionsFinal = mode === 'total' 
    ? totalCount 
    : Object.values(perTopicCounts).reduce((a, b) => a + (parseInt(b) || 0), 0);

  const canGenerate = selectedTopicLabels.length > 0 && totalQuestionsFinal > 0;

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
  };

  const handleResetSubject = () => {
    setSelectedSubject(null);
    setTreeData([]);
    setSelectedChapters({});
    setPerTopicCounts({});
    setShowAllTopics(false);
  };

  const handleGenerate = () => {
    if (!canGenerate) return;
    
    navigate('/practice-generator/result', {
      state: {
        subject: selectedSubject,
        selectedTopics: selectedTopicLabels,
        mode,
        totalQuestions: totalQuestionsFinal,
        perTopicCounts: mode === 'per-topic' ? perTopicCounts : null
      }
    });
  };

  return (
    <>
      <div className="breadcrum-header">
        <Container>
          <CustomBreadcrumb breadcrumbItems={breadcrumbItems} />
        </Container>
      </div>

      <div className="practice-generator subject-section">
        <Container>
          <h2>Practice Generator</h2>

          <Row>
            {/* Left Column: Configuration */}
            <Col lg={8}>
              
              {/* Step 1: Subject Selection */}
              <div className={`generator-step ${selectedSubject ? 'completed' : ''}`}>
                <div className="step-header">
                  <div className="header-left">
                    <div className="step-number">1</div>
                    <h3>Select Subject</h3>
                  </div>
                </div>
                
                {!selectedSubject ? (
                  <>
                    {loadingSubjects ? (
                      <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    ) : subjectError ? (
                      <Alert variant="danger">{subjectError}</Alert>
                    ) : (
                      <Row>
                        {subjects.map(subject => (
                          <Col className="mb-4" sm={6} xl={6} key={subject._id}>
                            <div className="subject-card-wrapper">
                              <SubjectCard
                                onClick={() => handleSubjectSelect(subject)}
                                iconSrc={<>{bookOpenIcon}</>}
                                imgSrc={subject.imageUrl || subject.image}
                                subjectName={subject.title}
                                subjectDate={new Date(subject.createdAt).getFullYear()}
                                chapters={
                                  <span>
                                    {subject.topics && subject.topics.length > 0
                                      ? `${subject.topics.length} Chapters`
                                      : "0 Chapters"}
                                  </span>
                                }
                              />
                            </div>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </>
                ) : (
                  <div className="selected-subject-summary">
                    <div className="icon-wrap"><FaCheckCircle /></div>
                    <h4>{selectedSubject.title}</h4>
                    <Button variant="link" size="sm" onClick={handleResetSubject} className="ms-auto text-decoration-none" style={{color: '#634b0e', fontWeight: 600}}>
                      <FaUndo className="me-2" /> Change Subject
                    </Button>
                  </div>
                )}
              </div>

              {/* Step 2 & 3: Visible only if subject is selected */}
              {selectedSubject && (
                <>
                  {/* Step 2: Topic Selection */}
                  <div className="generator-step">
                    <div className="step-header">
                      <div className="header-left">
                        <div className="step-number">2</div>
                        <h3>Select Topics</h3>
                      </div>
                    </div>
                    
                    {loadingTree ? (
                      <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted fw-semibold">Loading syllabus...</p>
                      </div>
                    ) : treeError ? (
                      <Alert variant="danger">{treeError}</Alert>
                    ) : treeData.length > 0 ? (
                      <div className="topic-tree-wrapper">
                        <FilterTopicTree
                          treeData={treeData}
                          levelLabels={EXAM_SYLLABUS_LEVELS}
                          selectedChapters={selectedChapters}
                          setSelectedChapters={setSelectedChapters}
                        />
                      </div>
                    ) : (
                      <Alert variant="warning">No topics found for this subject.</Alert>
                    )}
                  </div>

                  {/* Step 3: Question Count */}
                  <div className="generator-step">
                    <div className="step-header">
                      <div className="header-left">
                        <div className="step-number">3</div>
                        <h3>Question Configuration</h3>
                      </div>
                    </div>
                    
                    <div className="config-section">
                      <div className="mode-selector">
                        <button 
                          className={`btn-mode ${mode === 'total' ? 'active' : ''}`}
                          onClick={() => setMode('total')}
                        >
                          Total Questions
                        </button>
                        <button 
                          className={`btn-mode ${mode === 'per-topic' ? 'active' : ''}`}
                          onClick={() => setMode('per-topic')}
                          disabled={selectedTopicLabels.length === 0}
                        >
                          Per Topic
                        </button>
                      </div>

                      {mode === 'total' ? (
                        <div className="count-input-group">
                          <Form.Group>
                            <Form.Label>Number of questions</Form.Label>
                            <Form.Control 
                              type="number" 
                              value={totalCount} 
                              onChange={(e) => setTotalCount(parseInt(e.target.value) || 0)}
                              min="1"
                              max="100"
                            />
                            <Form.Text className="text-muted mt-3 d-block">
                              Questions will be distributed across the selected topics automatically.
                            </Form.Text>
                          </Form.Group>
                        </div>
                      ) : (
                        <div className="per-topic-inputs">
                          {selectedTopicLabels.length > 0 ? (
                            selectedTopicLabels.map(topic => (
                              <div key={topic.id} className="topic-input-row">
                                <span className="topic-label">{topic.label}</span>
                                <Form.Control 
                                  type="number" 
                                  placeholder="0"
                                  min="0"
                                  value={perTopicCounts[topic.id] || ''}
                                  onChange={(e) => setPerTopicCounts({...perTopicCounts, [topic.id]: parseInt(e.target.value) || 0})}
                                />
                              </div>
                            ))
                          ) : (
                            <div className="empty-state">
                              <FaInfoCircle className="icon" />
                              <p>Please select topics first to set individual question counts.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </Col>

            {/* Right Column: Summary & Actions */}
            <Col lg={4}>
              <div className="summary-panel">
                <div className="card-title">
                  <FaFileAlt /> Summary
                </div>
                
                <div className="summary-item">
                  <span className="label">Subject</span>
                  <span className="value">{selectedSubject ? selectedSubject.title : '-'}</span>
                </div>

                <div className="summary-item">
                  <span className="label">Selected Units</span>
                  <span className="value">{selectedTopicLabels.length}</span>
                </div>

                {selectedTopicLabels.length > 0 && (
                  <div className="selected-chips">
                    {(showAllTopics ? selectedTopicLabels : selectedTopicLabels.slice(0, 4)).map(topic => (
                      <span key={topic.id} className="chip">{topic.label}</span>
                    ))}
                    {selectedTopicLabels.length > 4 && !showAllTopics && (
                      <button className="chip" onClick={() => setShowAllTopics(true)}>+{selectedTopicLabels.length - 4} more</button>
                    )}
                  </div>
                )}

                <div className="summary-item mt-2">
                  <span className="label">Mode</span>
                  <span className="value">{mode === 'total' ? 'Fixed Total' : 'Per Topic'}</span>
                </div>
                
                <div className="total-questions-display">
                  <div className="number">{totalQuestionsFinal}</div>
                  <div className="label">Total Questions</div>
                </div>

                <div className="summary-item">
                  <span className="label">Estimated Pages</span>
                  <span className="value">{Math.ceil(totalQuestionsFinal / 4) || 0}</span>
                </div>

                <div className="summary-item mb-4">
                  <span className="label">Status</span>
                  {canGenerate ? (
                    <span className="value text-success">Ready to generate</span>
                  ) : (
                    <span className="value text-warning">Incomplete</span>
                  )}
                </div>

                <div className="action-buttons">
                  <Button 
                    variant="primary" 
                    className="btn-generate"
                    disabled={!canGenerate}
                    onClick={handleGenerate}
                  >
                    Generate Practice Paper
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default PracticeGenerator;
